import {
  disableBodyScroll,
  enableBodyScroll,
  clearAllBodyScrollLocks
} from 'body-scroll-lock'
import A11yDialog from 'a11y-dialog'

import {
  getTransitionDuration,
  viewportSize
} from './utils.js'


let enableConsoleLogs = false
let logger = (...msg) => enableConsoleLogs && console.log('%cSelectionPanel:', 'color:violet;', ...msg)


export default class SelectionPanel {
  constructor(panelElement, options = {}) {
    logger(this)

    this.el = panelElement
    this.innerEl = this.el.querySelector('header') || this.el.querySelector('[data-panel-inner]')
    this.scrollEl = this.el.querySelector('[data-panel-scroll]')
    this.options = options

    this.breakpoints = this.options.breakpoints || undefined
    this.setAnimationDuration(this.options)

    this.setupEvent = new CustomEvent('selectionpanel', {
      detail: { isSetup: true }
    })
    this.destroyedEvent = new CustomEvent('selectionpanel', {
      detail: { isSetup: false }
    })
    this.openedEvent = new CustomEvent('selectionpanel:opened')
    this.closingEvent = new CustomEvent('selectionpanel:closing')
    this.closedEvent = new CustomEvent('selectionpanel:closed')

    this.openCallback = this.open.bind(this)
    this.closeCallback = this.close.bind(this)

    if (this.breakpoints) {
      // Watch for viewport changes
      viewportSize(this.breakpoints.sm, this.breakpoints.lg).isSm.addListener(this.handleBreakpoint.bind(this))

      if (viewportSize(this.breakpoints.sm, this.breakpoints.lg).isSm.matches) {
        this.setupDialog()
      }
    }
  }

  handleBreakpoint(event) {
    if (event.matches) {
      // Check for animation duration again in case it's scoped
      // only to the smaller breakpoint:
      this.setAnimationDuration()
      this.setupDialog()
    } else {
      this.destroyDialog()
    }
  }

  setAnimationDuration() {
    if (!this.options.animationDuration) {
      // Use requestAnimationFrame to wait until the browser has completed
      // at least one rendering cycle, to be sure that CSS class values
      // will be available:
      requestAnimationFrame(() => {
        this.animationDuration = getTransitionDuration(this.el)
      })
    } else {
      this.animationDuration = this.options.animationDuration
    }
  }

  setupDialog() {
    logger('setting up panel dialog')

    this.el.setAttribute('hidden', 'true')

    this.closeBtn = this.closeBtnTemplate()
    this.innerEl.prepend(this.closeBtn)

    this.dialog = new A11yDialog(this.el)

    // Now a11y-dialog can take care of hiding/showing
    this.el.hidden = false

    document.addEventListener('click', this.openCallback)
    document.addEventListener('click', this.closeCallback)

    this.dialog.on('hide', (event) => {
      // Call close() first to handle everything properly.
      if (event.detail?.key === 'Escape' && this.dialog.shown) {
        event.preventDefault()
        this.close(event)
        return
      }
    })

    this.el.dispatchEvent(this.setupEvent)
  }

  destroyDialog() {
    this.close()

    // Keep an array of the attributes that a11y-dialog adds to the dialog element,
    // so that we can easily clean them up when we need to destroy the dialog
    let a11yDialogAttrs = [
      'aria-hidden',
      'aria-modal',
      'role',
      'tabindex'
    ]

    // Tear it all down, including the aforementioned a11y-dialog attributes

    // a11y-dialog's destroy method removes its own event listeners
    // Pass in `false` to prevent cloning and replacing this.filterForm
    this.dialog.destroy(false)

    // Clean up whatever's left
    a11yDialogAttrs.forEach(attr => {
      logger('removing attribute', attr, 'from', this.el)
      this.el.removeAttribute(attr)
    })

    clearAllBodyScrollLocks()

    this.el.hidden = false

    document.removeEventListener('click', this.openCallback)
    document.removeEventListener('click', this.closeCallback)

    // Clean up close button
    this.closeBtn.remove()

    this.el.dispatchEvent(this.destroyedEvent)
  }

  open(event) {
    let targetIsOpenBtn = event.target.dataset.open === this.el.id

    if (!targetIsOpenBtn) return false

    disableBodyScroll(this.scrollEl)
    this.dialog.show()

    // Make sure this class is removed
    this.el.classList.remove('is-closing')

    // Animate it
    this.el.classList.add('is-open')

    this.el.dispatchEvent(this.openedEvent)
  }

  close(event) {
    let targetIsBackdrop = event?.target === this.el
    let targetIsCloseBtn = event?.target.dataset.close === this.el.id

    if (
      event?.type === 'click'
        && !targetIsBackdrop
        && !targetIsCloseBtn
    ) {
      return false
    }

    enableBodyScroll(this.scrollEl)

    // Animate the dialog closing
    this.el.classList.add('is-closing')
    this.el.classList.remove('is-open')

    this.el.dispatchEvent(this.closingEvent)

    // If there's an animation duration, give it time to finish before hiding dialog
    setTimeout(() => {
      this.el.classList.remove('is-closing')
      this.dialog.hide()
    }, this.animationDuration)

    this.el.dispatchEvent(this.closedEvent)
  }

  closeBtnTemplate() {
    let btn = document.createElement('button')
    btn.setAttribute('type', 'button')
    btn.setAttribute('data-close', this.el.id)
    btn.classList.add('btn', 'btn-sm', 'panel-close')
    btn.innerHTML = `<span class="u-vh">${gettext('Close')}</span><svg class="icon" height="21" viewBox="6 6 9 9" width="21" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><g fill="none" fill-rule="evenodd" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"><path d="m7.5 7.5 6 6"/><path d="m13.5 7.5-6 6"/></g></svg>`
    return btn
  }


}
